import { useState } from "react";
import { X, Download, FileDown, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api, fmtCHF, fmtDate, downloadFile, formatApiError } from "@/lib/api";
import { FACT_TYPE_LABELS, PAYMENT_STATUS_LABELS } from "@/lib/organs";
import { SectionTitle } from "@/components/Shared";
import PipelineStepper from "@/components/PipelineStepper";
import SampleContent from "@/components/SampleContent";

export default function DocDetail({ detail, onClose, onChanged }) {
  const [declaring, setDeclaring] = useState(false);
  const [declMethod, setDeclMethod] = useState("");
  const [declDate, setDeclDate] = useState("");
  if (!detail) return null;

  const canDeclarePaid = detail.doc_type === "Fattura fornitore" && (detail.payment_status || "open") === "open";

  const declarePaid = async () => {
    try {
      await api.post(`/documents/${detail.doc_id}/declare-paid`, {
        payment_method: declMethod || null, payment_date: declDate || null,
      });
      toast.success("Pagamento dichiarato registrato (fonte: conferma umana). Riconciliazione in attesa.");
      setDeclaring(false);
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const downloadOriginal = async () => {
    try {
      await downloadFile(`/documents/${detail.doc_id}/file`, detail.filename);
      toast.success("Originale scaricato · integrità verificata via checksum");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Originale non disponibile");
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/60 backdrop-blur-sm" data-testid="doc-detail-overlay" onClick={onClose}>
      <div className="w-full max-w-md h-full glass-strong overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate" data-testid="doc-detail-title">{detail.supplier || detail.filename}</div>
            <div className="text-xs text-gray-500">{detail.doc_type} · {fmtDate(detail.date)} · <span className="font-mono">{fmtCHF(detail.amount)}</span></div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 shrink-0" data-testid="doc-detail-close"><X size={18} /></button>
        </div>

        {detail.note && <div className="text-xs text-amber-300/90 bg-amber-400/5 border border-amber-400/15 rounded-xl px-3 py-2">{detail.note}</div>}
        <SampleContent detail={detail} />

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
          {detail.actual_payment_method && <div><span className="text-gray-600">Metodo: </span><span className="text-gray-300">{detail.actual_payment_method}</span></div>}
          {detail.payment_status && (
            <div data-testid="doc-payment-status"><span className="text-gray-600">Pagamento: </span>
              <span style={{ color: (PAYMENT_STATUS_LABELS[detail.payment_status] || {}).color }}>{(PAYMENT_STATUS_LABELS[detail.payment_status] || {}).label || detail.payment_status}</span>
            </div>
          )}
          {detail.expected_payment_method && <div><span className="text-gray-600">Previsto: </span><span className="text-gray-300">{detail.expected_payment_method}</span></div>}
          {detail.payment_terms_days != null && <div><span className="text-gray-600">Termini: </span><span className="text-gray-300">{detail.payment_terms_days} giorni</span></div>}
          {detail.invoice_number && <div><span className="text-gray-600">N. fattura: </span><span className="text-gray-300">{detail.invoice_number}</span></div>}
          {detail.due_date && <div><span className="text-gray-600">Scadenza: </span><span className="text-gray-300">{fmtDate(detail.due_date)}</span></div>}
          {detail.person && <div><span className="text-gray-600">Persona: </span><span className="text-gray-300">{detail.person}</span></div>}
          {detail.management_category && <div><span className="text-gray-600">Gestionale: </span><span className="text-gray-300">{detail.management_category}</span></div>}
          {detail.accounting_category && <div><span className="text-gray-600">Contabile: </span><span className="text-gray-300">{detail.accounting_category}</span></div>}
        </div>

        {canDeclarePaid && (
          <div className="rounded-2xl border border-amber-400/20 bg-amber-400/5 p-4" data-testid="declare-paid-box">
            {!declaring ? (
              <button data-testid="declare-paid-open" onClick={() => setDeclaring(true)} className="btn-ghost !text-xs w-full">
                <BadgeCheck size={13} /> Dichiara questa fattura come pagata
              </button>
            ) : (
              <div className="space-y-3">
                <p className="text-[11px] text-amber-200/80 leading-relaxed">
                  La tua dichiarazione viene registrata come fonte umana separata — non crea alcuna evidenza bancaria.
                  La riconciliazione resterà in attesa fino alla prova bancaria/documentale.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {[["banca", "Bonifico bancario"], ["contanti", "Contanti"], ["carta_aziendale", "Carta aziendale"], ["altro", "Altro"]].map(([v, l]) => (
                    <button key={v} data-testid={`declare-method-${v}`} onClick={() => setDeclMethod(v)}
                      className={`chip !cursor-pointer ${declMethod === v ? "!text-cyan-200 !border-cyan-400/60 !bg-cyan-400/15" : ""}`}>{l}</button>
                  ))}
                </div>
                <input type="date" data-testid="declare-date" value={declDate} onChange={(e) => setDeclDate(e.target.value)} className="input-dark !py-2 !text-xs" />
                <div className="flex gap-2">
                  <button data-testid="declare-paid-submit" onClick={declarePaid} className="btn-primary !py-2 !text-xs flex-1">Registra dichiarazione</button>
                  <button onClick={() => setDeclaring(false)} className="btn-ghost !py-2 !text-xs">Annulla</button>
                </div>
              </div>
            )}
          </div>
        )}

        <div>
          <SectionTitle>Pipeline canonica</SectionTitle>
          <PipelineStepper pipeline={detail.pipeline || []} detailed />
        </div>

        <div>
          <SectionTitle>Riferimento originale</SectionTitle>
          {detail.storage_ref?.checksum ? (
            <div className="text-[11px] font-mono text-gray-400 space-y-1" data-testid="doc-detail-storage">
              <div>provider: {detail.storage_ref.storage_provider}</div>
              <div className="break-all">object_key: {detail.storage_ref.object_key}</div>
              <div className="break-all">sha256: {detail.storage_ref.checksum}</div>
              <div>versione: {detail.storage_ref.version} · {detail.storage_ref.sync_status}</div>
            </div>
          ) : (
            <p className="text-[11px] text-gray-500" data-testid="doc-detail-legacy">
              Originale non conservato (documento pre-Phase 2). Presenti solo metadati.
            </p>
          )}
        </div>

        <div>
          <SectionTitle>Evidenze collegate</SectionTitle>
          {(detail.evidence || []).length === 0 && <p className="text-[11px] text-gray-500">Nessuna evidenza.</p>}
          {(detail.evidence || []).map((ev) => (
            <div key={ev.evidence_id} className="text-[11px] text-gray-400 rounded-xl border border-white/5 px-3 py-2 mb-1.5">
              {FACT_TYPE_LABELS[ev.business_fact_type] || ev.business_fact_type} · {ev.verification_status}{ev.verification_mode ? ` (${ev.verification_mode})` : ""} · {ev.accounting_status}
            </div>
          ))}
        </div>

        {((detail.audit || []).length > 0 || (detail.confirmations || []).length > 0) && (
          <div data-testid="doc-detail-audit">
            <SectionTitle>Audit trail</SectionTitle>
            {(detail.confirmations || []).map((c) => (
              <div key={c.confirmation_id} className="text-[11px] text-cyan-200/80 rounded-xl border border-cyan-400/15 bg-cyan-400/5 px-3 py-2 mb-1.5"
                data-testid={`confirmation-${c.confirmation_id}`}>
                Conferma umana · {c.field}: {c.previous == null ? "—" : String(c.previous)} → {c.deferred ? "rimandato" : String(c.new)} · {c.source}
              </div>
            ))}
            {(detail.audit || []).map((a, i) => (
              <div key={i} className="text-[11px] text-gray-500 px-3 py-1.5">
                {a.event}{a.details ? ` · ${a.details}` : ""} · {new Date(a.at).toLocaleString("it-IT")}
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 pt-2">
          <button data-testid="doc-detail-download" onClick={downloadOriginal} className="btn-ghost !text-xs" disabled={!detail.storage_ref?.checksum}>
            <Download size={13} /> Scarica originale
          </button>
          <button data-testid="doc-detail-export" onClick={() => downloadFile(`/documents/${detail.doc_id}/export`, `dad-doc-${detail.doc_id}.json`).then(() => toast.success("Export documento scaricato"))} className="btn-ghost !text-xs">
            <FileDown size={13} /> Esporta JSON
          </button>
        </div>
      </div>
    </div>
  );
}
